import React from 'react'
import { Link, useLocation } from 'react-router-dom'
import { motion } from 'framer-motion'
import { 
  Home, 
  Trophy, 
  Users, 
  User,
  ChevronLeft,
  ChevronRight,
  MessageCircle,
  TrendingUp,
  Hash,
  Star
} from 'lucide-react'
import { useStore } from '../../store/useStore'
import { useTwitterMentions, useTrendingHashtags } from '../../hooks/useTwitterData'

const Sidebar = () => {
  const location = useLocation()
  const { sidebarOpen, setSidebarOpen } = useStore()
  
  // Get real Twitter data for sidebar stats
  const { data: tweets = [] } = useTwitterMentions(50)
  const { data: hashtags = [] } = useTrendingHashtags()

  const menuItems = [
    { path: '/', icon: Home, label: 'Dashboard' },
    { path: '/leaderboard', icon: Trophy, label: 'Leaderboard' },
    { path: '/community', icon: Users, label: 'Community' },
    { path: '/profile', icon: User, label: 'Profile' },
  ]

  // Calculate social metrics from real Twitter data
  const socialStats = [
    { 
      label: 'Tweets Today', 
      value: tweets.filter((t: any) => 
        new Date(t.timestamp) > new Date(Date.now() - 24 * 60 * 60 * 1000)
      ).length.toString(),
      icon: MessageCircle,
      color: 'text-primary-400'
    },
    { 
      label: 'Total Engagement', 
      value: tweets.reduce((sum: number, t: any) => sum + t.likes + t.retweets, 0).toString(),
      icon: TrendingUp,
      color: 'text-green-400'
    },
    { 
      label: 'Active Users', 
      value: new Set(tweets.map((t: any) => t.username)).size.toString(),
      icon: Users,
      color: 'text-lunatic-400'
    },
  ]

  return (
    <motion.div
      initial={{ x: -300 }}
      animate={{ x: 0 }}
      transition={{ type: 'spring', stiffness: 300, damping: 30 }}
      className={`fixed left-0 top-16 h-[calc(100vh-4rem)] bg-dark-900/95 backdrop-blur-md border-r border-dark-700 transition-all duration-300 z-40 ${
        sidebarOpen ? 'w-64' : 'w-16'
      }`}
    >
      <div className="flex flex-col h-full">
        {/* Toggle Button */}
        <div className="flex justify-end p-4">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 rounded-lg bg-dark-800 hover:bg-dark-700 transition-colors"
          >
            {sidebarOpen ? (
              <ChevronLeft className="w-4 h-4 text-dark-400" />
            ) : (
              <ChevronRight className="w-4 h-4 text-dark-400" />
            )}
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-4 pb-4 space-y-2">
          {menuItems.map(({ path, icon: Icon, label }) => (
            <Link
              key={path}
              to={path}
              className={`flex items-center space-x-3 px-3 py-3 rounded-lg transition-all duration-300 group ${
                location.pathname === path
                  ? 'bg-lunatic-500/20 text-lunatic-400 border border-lunatic-500/30'
                  : 'text-dark-300 hover:bg-dark-800 hover:text-white'
              }`}
            >
              <Icon className="w-5 h-5 flex-shrink-0" />
              {sidebarOpen && (
                <span className="font-medium">{label}</span>
              )}
            </Link>
          ))}
        </nav>

        {/* Social Stats */}
        {sidebarOpen && (
          <div className="px-4 pb-4 space-y-4">
            <div className="border-t border-dark-700 pt-4">
              <h3 className="text-sm font-semibold text-dark-300 mb-3">
                Social Activity
              </h3>
              <div className="space-y-3">
                {socialStats.map(({ label, value, icon: Icon, color }) => (
                  <div
                    key={label}
                    className="flex items-center justify-between p-3 bg-dark-800/50 rounded-lg"
                  >
                    <div className="flex items-center space-x-2">
                      <Icon className={`w-4 h-4 ${color}`} />
                      <span className="text-xs text-dark-400">{label}</span>
                    </div>
                    <span className={`text-sm font-semibold ${color}`}>
                      {value}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Trending Hashtags Preview */}
            {hashtags.length > 0 && (
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-dark-300">
                  Trending Now
                </h3>
                                 <div className="space-y-2">
                   {hashtags.slice(0, 3).map((hashtag: any) => (
                     <div
                       key={hashtag.tag}
                       className="flex items-center justify-between p-2 bg-dark-800/30 rounded-lg"
                     >
                       <div className="flex items-center space-x-2">
                         <Hash className="w-3 h-3 text-lunatic-400" />
                         <span className="text-xs text-white">{hashtag.tag}</span>
                       </div>
                       <span className="text-xs text-green-400">{hashtag.change}</span>
                     </div>
                   ))}
                </div>
              </div>
            )}

            {/* Community Status */}
            <div className="bg-lunatic-gradient rounded-lg p-3">
              <div className="flex items-center space-x-2 mb-2">
                <Star className="w-4 h-4 text-white" />
                <span className="text-sm font-semibold text-white">
                  Community Score
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="flex-1 bg-white/20 rounded-full h-2">
                  <div 
                    className="bg-white rounded-full h-2 transition-all duration-300"
                    style={{ width: `${Math.min((tweets.length / 50) * 100, 100)}%` }}
                  />
                </div>
                <span className="text-xs text-white font-medium">
                  {Math.round((tweets.length / 50) * 100)}%
                </span>
              </div>
              <p className="text-xs text-white/80 mt-1">
                {tweets.length} mentions tracked
              </p>
            </div>
          </div>
        )}
      </div>
    </motion.div>
  )
}

export default Sidebar 